// id.ts — Indonesian catalog for the owner dashboard.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md (binding tone, do-not-
// translate list, plural rule) and docs/superpowers/specs/locale-glossary-vi.md §2 (imported
// do-not-translate list). Register: "kamu" throughout (direct, modern-SaaS, maker-to-maker —
// not the formal "Anda" service register); "kami" only where the English genuinely speaks as
// Embent/the team in first person. See translator-id-report.md for the full rationale.
//
// Function-valued entries keep their exact parameter names and interpolate every argument they
// need; Indonesian has no plural inflection, so every `n === 1 ? 'x' : 'xs'` ternary in en.ts
// collapses to one fixed form here — the parameter itself is still threaded through the
// signature even where it no longer drives a branch.
import { type Catalog } from './en';

export const id = {
  common: {
    close: 'Tutup',
  },
  status: {
    pending: 'menunggu',
    crawling: 'merayapi',
    indexing: 'mengindeks',
    ready: 'siap',
    error: 'galat',
    paused: 'dijeda',
    hint: 'petunjuk',
    deleting: 'menghapus',
  },
  time: {
    never: 'tidak pernah',
    justNow: 'baru saja',
    inAMoment: 'sesaat lagi',
    secondsAgo: ({ n }: { n: number }) => `${n} dtk lalu`,
    inSeconds: ({ n }: { n: number }) => `${n} dtk lagi`,
    minutesAgo: ({ n }: { n: number }) => `${n} mnt lalu`,
    inMinutes: ({ n }: { n: number }) => `${n} mnt lagi`,
    hoursAgo: ({ n }: { n: number }) => `${n} jam lalu`,
    inHours: ({ n }: { n: number }) => `${n} jam lagi`,
    daysAgo: ({ n }: { n: number }) => `${n} hari lalu`,
    inDays: ({ n }: { n: number }) => `${n} hari lagi`,
    monthsAgo: ({ n }: { n: number }) => `${n} bln lalu`,
    inMonths: ({ n }: { n: number }) => `${n} bln lagi`,
    yearsAgo: ({ n }: { n: number }) => `${n} thn lalu`,
    inYears: ({ n }: { n: number }) => `${n} thn lagi`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Melihat sebagai ${email} — kamu sedang mengimpersonasi pengguna ini.`,
    stopImpersonating: 'Berhenti',

    platformAdmin: 'Admin Platform',
    adminTabWorkspaces: 'Workspace',
    adminTabBots: 'Bot',
    adminTabJobs: 'Tugas',
    adminTabPosts: 'Postingan',
    adminTabFeedback: 'Masukan',
    adminTabPlayground: 'Playground',

    botsLoadError: 'Gagal memuat bot kamu.',
    botsLoadErrorInline: 'Gagal memuat bot kamu',
    reloadToRetry: 'Muat ulang halaman untuk mencoba lagi.',

    navOverview: 'Ringkasan',
    navUnanswered: 'Belum Terjawab',
    navConversations: 'Percakapan',
    navLeads: 'Lead',
    navSources: 'Sumber',
    navPlayground: 'Playground',
    navAppearance: 'Tampilan',
    navSettings: 'Pengaturan',
    groupNeedsYou: 'PERLU KAMU',
    groupKnowledge: 'PENGETAHUAN',
    groupSetup: 'PENYIAPAN',

    closeMenu: 'Tutup menu',
    navigationLabel: 'Navigasi',
    openNavigation: 'Buka navigasi',
    openAccountMenu: 'Buka menu akun',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, ganti bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `BOT KAMU${cap !== null ? ` · ${n} DARI ${cap} TERPAKAI` : ''}`,
    allBots: 'Semua bot',
    newBot: 'Bot baru',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Bot baru — ${n} dari ${cap} terpakai`,

    pagesIndexed: 'Halaman terindeks',

    feedbackTrigger: 'Masukan',
    feedbackTypeGroupLabel: 'Jenis masukan',
    feedbackTypeBug: 'Bug',
    feedbackTypeFeature: 'Permintaan fitur',
    feedbackTypeQuestion: 'Pertanyaan',
    feedbackPlaceholder: 'Apa yang terjadi, atau apa yang bisa membuat Embent lebih baik untukmu?',
    feedbackSent: 'Terkirim — kami membaca semuanya.',
    feedbackSendError: 'Gagal mengirim masukan.',
    feedbackSending: 'Mengirim…',
    feedbackSend: 'Kirim',
    feedbackDialogTitle: 'Kirim masukan',
    feedbackDialogDescription: 'Bug, permintaan fitur, atau pertanyaan — ini langsung masuk ke tim.',

    workspaceSettings: 'Pengaturan workspace',
    members: 'Anggota',
    billingUsage: 'Tagihan & pemakaian',
    switchWorkspace: 'Ganti workspace',
    accountLabelPrefix: 'Akun — ',
    account: 'Akun',
    signOut: 'Keluar',
    themePrefix: 'Tema: ',
    languagePrefix: 'Bahasa: ',
    nextPrefix: 'berikutnya: ',
    themeSystem: 'Sistem',
    themeDark: 'Gelap',
    themeLight: 'Terang',
    roleOwner: 'Pemilik',
    roleAdmin: 'Admin',
    roleMember: 'Anggota',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · kamu adalah ${role}` : `Kamu adalah ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, menu workspace dan akun`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Kamu sudah memakai ${pct}% pesan bulananmu — upgrade supaya semua percakapan tetap lancar.`,
    dismiss: 'Tutup',

    renewsOn: ({ date }: { date: string }) => `Diperpanjang ${date}`,
    resetsOn: ({ date }: { date: string }) => `Direset ${date}`,
    answersPaused: 'Jawaban baru dijeda — sudah lewat 110% dari batas',
    messagesLeft: ({ n }: { n: string }) => `Sisa ${n} pesan bulan ini`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} masih menampilkan lencana "Powered by Embent"`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} membuat lencana "Powered by Embent" opsional`,
    upgradeCta: ({ price }: { price: number }) => `Upgrade — $${price}/bln`,
    resumeAnswersCta: ({ price }: { price: number }) => `Lanjutkan jawaban — $${price}/bln`,
    resumeAnswersBenefit: 'Upgrade langsung melanjutkan jawaban',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} menambah ${messages} pesan, ${bots} bot, dan ${seats} kursi`,
  },
  bots: {
    noSources: 'tidak ada sumber',
    allSourcesReady: ({ n }: { n: number }) => `${n} · semua siap`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Dijeda',
    sourcesRemoving: ({ n }: { n: number }) => `${n} sumber · menghapus`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} sumber · galat sinkronisasi`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} sumber · menyinkronkan`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} psn · ${unanswered} belum terjawab`,
    botsCount: ({ n }: { n: number }) => `${n} bot`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} dari ${cap} terpakai`,

    colBot: 'Bot',
    colStatus: 'Status',
    colMessages14d: 'Pesan · 14h',
    colUnanswered: 'Belum terjawab',
    colSources: 'Sumber',
    pageTitle: 'Bot',
    emptyTitle: 'Buat bot pertamamu',
    emptyDescription:
      'Arahkan ke website atau dokumenmu — ia meng-crawl kontennya dan mulai menjawab pertanyaan pengunjung.',
    planIncludesBots: ({ cap }: { cap: number }) => `Paketmu mencakup ${cap} bot`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/bln) menjalankan hingga ${maxBots} bot — satu per situs, masing-masing dengan pengetahuan dan tampilannya sendiri.`,
    comparePlansLink: 'Bandingkan paket →',
    newBotDialogTitle: 'Bot baru',
    botNameFieldLabel: 'Nama',
    botNamePlaceholder: 'Bot Support Acme',
    creatingCta: 'Membuat…',
    createCta: 'Buat',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Kata sandi',
    genericFormError: 'Ada yang tidak beres. Coba lagi.',
    continueWithGoogle: 'Lanjutkan dengan Google',
    backToSignIn: 'Kembali ke halaman masuk',

    // SignIn.tsx
    signInTitle: 'Masuk',
    signInSubtitle: 'Selamat datang kembali di Embent.',
    passwordJustResetNotice: 'Kata sandi diperbarui — masuk dengan kata sandi barumu.',
    forgotPasswordLink: 'Lupa kata sandi?',
    unverifiedResendNotice: 'Cek inbox-mu — kami sudah mengirim ulang email verifikasinya.',
    signingIn: 'Sedang masuk…',
    signInCta: 'Masuk',
    noAccountPrompt: 'Belum punya akun? ',
    signUpLink: 'Daftar',

    // SignUp.tsx
    signUpTitle: 'Buat akunmu',
    signUpSubtitle: 'Mulai menjawab pertanyaan pengunjung dalam hitungan menit.',
    nameLabel: 'Nama',
    creatingAccount: 'Membuat akun…',
    createAccountCta: 'Buat akun',
    haveAccountPrompt: 'Sudah punya akun? ',
    signInLink: 'Masuk',

    // CheckEmail.tsx
    checkEmailTitle: 'Cek emailmu',
    checkEmailSubtitle: 'Kami sudah mengirim tautan verifikasi. Biasanya sampai dalam semenit.',
    checkEmailBody: 'Klik tautan di email itu untuk mengaktifkan akunmu.',

    // VerifyEmail.tsx
    verifyFailedTitle: 'Verifikasi gagal',
    verifiedTitle: 'Email terverifikasi',
    verifyFailedBody: 'Tautan verifikasi itu tidak valid atau sudah kedaluwarsa.',
    verifiedBody: 'Semua sudah siap — mengarahkanmu ke dashboard…',

    // RequestReset.tsx
    requestResetTitle: 'Reset kata sandimu',
    requestResetSubtitlePending: 'Masukkan emailmu dan kami akan mengirim tautan reset.',
    requestResetSubtitleSent: 'Kalau email itu punya akun Embent, kami sudah mengirim tautan reset.',
    requestResetBodySent: 'Cek inbox-mu. Tautannya kedaluwarsa dalam 1 jam.',
    sendingResetLink: 'Mengirim…',
    sendResetLinkCta: 'Kirim tautan reset',

    // ResetPassword.tsx
    resetLinkInvalidTitle: 'Tautan reset tidak valid',
    resetLinkInvalidBody: 'Tautan reset kata sandi ini kehilangan token-nya, atau sudah pernah dipakai.',
    requestNewLinkCta: 'Minta tautan baru',
    chooseNewPasswordTitle: 'Pilih kata sandi baru',
    chooseNewPasswordSubtitle: 'Buat yang belum pernah kamu pakai sebelumnya.',
    newPasswordLabel: 'Kata sandi baru',
    resetLinkExpiredFallback: 'Tautan reset itu tidak valid atau sudah kedaluwarsa.',
    savingPassword: 'Menyimpan…',
    saveNewPasswordCta: 'Simpan kata sandi baru',

    // InviteAccept.tsx
    inviteNotValidTitle: 'Undangan tidak valid',
    goToEmbentLink: 'Ke Embent',
    inviteInvalidFallback: 'Undangan ini sudah tidak berlaku lagi.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Kamu diundang ke ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Dikirim ke ${email} — masuk atau buat akun dengan email itu untuk menerima.`,
    inviteSignInCta: 'Masuk',
    inviteSignUpCta: 'Buat akun',
    wrongAccountTitle: 'Akun salah',
    signOutCta: 'Keluar',
    inviteAcceptFallback: 'Ada yang tidak beres saat menerima undangan ini.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Bergabung ke ${workspaceName}…`,
    joiningWorkspaceBody: 'Sebentar ya.',

    eventLabels: {
      newLead: 'lead baru',
      unansweredDigest: 'ringkasan belum terjawab',
      crawlFailed: 'crawl gagal',
      weeklySummary: 'ringkasan mingguan',
    },
    unsubscribeLinkExpiredTitle: 'Tautan ini sudah kedaluwarsa',
    unsubscribeLinkExpiredBody: 'Atur notifikasi dari pengaturan bot di dashboard.',
    openDashboardCta: 'Buka dashboard',
    unsubscribedTitle: 'Kamu sudah berhenti berlangganan',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Kamu tidak akan lagi menerima email ${eventLabel} untuk ${botName ?? 'bot ini'}. Kamu bisa menyalakannya lagi di pengaturan bot.`,

    // NotFound.tsx
    notFoundEyebrow: 'Halaman tidak ditemukan',
    notFoundTitle: 'Tidak ada apa-apa di alamat ini',
    notFoundBody: 'Tautannya mungkin sudah usang, atau halamannya sudah dipindah.',
    notFoundCta: 'Ke bot-mu',

    // LandingResolver.tsx
    landingBotsErrorFallback: 'Ada yang tidak beres saat memuat bot kamu.',
    landingGoToBotsCta: 'Ke bot kamu →',
  },
  onboarding: {
    skipForNow: 'Lewati dulu',
    invalidWebsiteUrl: 'Masukkan URL website yang valid, mis. https://acme.id',
    websiteUrlLabel: 'URL website',
    genericFallbackError: 'Ada yang tidak beres.',

    // Onboarding.tsx
    crawlDefaultHostname: 'situsmu',
    noSourceToReplace: 'Belum ada sumber untuk diganti.',

    // Stepper.tsx
    stepWebsite: 'Website',
    stepCrawl: 'Crawl',
    stepTryIt: 'Coba',
    stepLook: 'Tampilan',
    stepGoLive: 'Live',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Langkah ${current} dari ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Progres onboarding: langkah ${current} dari ${total}, ${label}`,
    stepTablistAriaLabel: 'Progres onboarding',

    // WebsiteStep.tsx
    websiteHeading: 'Tambahkan websitemu',
    websiteSubtitle:
      'Embent membaca situsmu dan membangun bot yang menjawab dengan kutipan sumber — tanpa mengarang. Kebanyakan situs siap dalam beberapa menit.',
    botNameLabel: 'Nama bot',
    botNamePlaceholder: 'Support Acme',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'Kami akan menemukan halamanmu secara otomatis. Kamu bisa menambah sumber lain nanti.',
    botNameRequired: 'Masukkan nama untuk bot-mu.',
    websiteStarting: 'Memulai…',
    websiteSubmitCta: 'Mulai baca situsku',

    // CrawlStep.tsx
    crawlHeading: ({ hostname }: { hostname: string }) => `Membaca ${hostname}…`,
    crawlSubtitleReady: 'Semua selesai — setiap halaman terindeks dan siap menjawab.',
    crawlSubtitleSyncing: 'Kamu tidak perlu menunggu di sini — coba bot-mu sambil ini selesai.',
    crawlStatusLines: {
      pending: ['Antre — akan segera dimulai…', 'Bersiap untuk mulai…'],
      crawling: ['Menemukan halaman di situs…', 'Mengikuti tautan…', 'Membaca halaman…'],
      indexing: ['Membangun indeks pencarian…', 'Memproses yang sudah dibaca…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} halaman terindeks`,
    crawlNoSourceYet: 'Kami belum menemukan sumber untuk bot ini.',
    crawlErrorLead: 'Penyebab paling mungkin adalah salah ketik di alamatnya — cek lagi di bawah dan coba lagi.',
    crawlReplaceUrlCta: 'Coba alamat lain',
    crawlReplaceUrlUpdating: 'Memperbarui…',
    crawlReplaceUrlHelper: 'Kami akan menghapus percobaan yang gagal dan mulai dari awal dengan alamat ini.',
    crawlRetryCta: 'Coba lagi',
    crawlRetrying: 'Mencoba lagi…',
    crawlRetryHelper:
      'Kalau alamatnya sudah benar dan situsnya cuma sempat tidak terjangkau, coba lagi saja. Situs ' +
      'yang butuh JavaScript untuk menampilkan kontennya bisa diatasi dengan pengaturan yang tersedia setelah ' +
      'penyiapan, di layar Sumber.',
    crawlCtaSyncing: 'Coba bot-mu sambil ini selesai →',
    crawlCtaReady: 'Selanjutnya: coba bot-mu →',

    // TryItStep.tsx
    tryItHeading: 'Tanyakan apa saja',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Ia sudah tahu ${n} halaman — dan masih terus mempelajari sisanya. Setiap jawaban menampilkan sumbernya.`,
    tryItSubtitleNoPages: 'Ia masih membaca situsmu — coba tanyakan saja; jawabannya cuma akan makin baik dari sini.',
    tryItSamplePrompt: `Coba: "Apa kebijakan pengembalian barangmu?"`,
    tryItPlaceholder: 'Tanyakan soal pengiriman, harga, apa saja…',
    tryItSending: 'Mengirim…',
    tryItSendCta: 'Kirim',
    tryItNextCta: 'Sudah bagus — jadikan milikku',

    // LookStep.tsx
    lookHeading: 'Sesuaikan dengan situsmu',
    lookSubtitle:
      'Pilih warna brand-mu — pratinjaunya berubah saat kamu mengetik. Kamu bisa menyesuaikan semuanya lagi nanti di Tampilan.',
    brandColorLabel: 'Warna brand',
    colorHexAriaLabel: 'Nilai hex warna',
    welcomeMessageLabel: 'Pesan sambutan',
    lookSaving: 'Menyimpan…',
    lookContinueCta: 'Lanjut',

    // GoLiveStep.tsx
    goLiveHeading: 'Satu cuplikan kode dan kamu langsung live',
    goLivePasteBeforePrefix: 'Tempel ini sebelum tag penutup',
    goLivePasteBeforeSuffix: 'di situsmu. Selesai — bot-nya muncul di kanan bawah.',
    goLiveInstallGuidesLink: 'Panduan instalasi per platform',
    goLiveFirstAnswerNotice: 'Kami akan mengirim email begitu widget-nya menjawab pengunjung pertamanya.',
    goLiveDashboardCta: 'Ke dashboard',
    goLiveViewSourcesCta: 'Lihat sumber',
  },
  overview: {
    title: 'Ringkasan',
    liveOnPrefix: 'Live di ',
    answeringSince: ({ date }: { date: string }) => `menjawab sejak ${date}`,
    testInPlaygroundCta: 'Coba di Playground',
    addSourcesCta: 'Tambah sumber',
    botDetailsLoadError: ({ message }: { message: string }) => `Gagal memuat detail bot — ${message}`,

    messagesLabel: 'Pesan · 14 hari terakhir',
    conversationsLink: 'Percakapan →',
    vsPreviousPeriod: 'vs 14 hari sebelumnya',
    leadsLabel: 'Lead tertangkap',
    newLeadsCount: ({ n }: { n: number }) => `${n} baru`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} minggu ini`,
    viewLeadsLink: 'Lihat lead →',
    unansweredLabel: 'Pertanyaan belum terjawab',
    fixThemLink: 'Perbaiki →',

    knowledgeHeading: 'Pengetahuan',
    pointAtSite: 'Arahkan ke situsmu',
    addSourceLink: 'Tambah sumber →',
    addSourcesLink: 'Tambah sumber →',
    pagesIndexed: 'Halaman terindeks',
    sourcesCountLabel: 'Sumber',
    lastSyncedLabel: 'Terakhir sinkron',
    manageSourcesLink: 'Kelola sumber →',
    resyncing: 'Menyinkronkan ulang…',
    allSyncing: 'Semua sedang sinkron',
    resyncCta: 'Sinkron ulang',
    resyncNothingToSync: 'Tidak ada yang perlu disinkronkan — semua sumber sudah sinkron.',
    resyncGenericError: 'Gagal memulai sinkron ulang — coba lagi.',

    latestConversationsHeading: 'Percakapan terbaru',
    allConversationsLink: 'Semua percakapan →',
    noConversationsEmpty: 'Sepi dalam 14 hari terakhir — percakapan baru akan muncul di sini.',
    noMessageFallback: '(tidak ada pesan)',
    leadCapturedTag: 'Lead tertangkap',
    hasUnansweredTag: 'Ada yang belum terjawab',

    knowledgeAllReady: 'Semua siap',
    knowledgeSyncing: 'Sumber sedang sinkron',
    knowledgeNeedsAttention: 'Ada sumber yang perlu diperiksa',
    knowledgeRemoving: 'Menghapus sumber',

    // FirstRun.tsx
    createdSubtitle: ({ time }: { time: string }) => `Dibuat ${time}`,
    zeroStatMessagesHint: 'Mulai sejak pertanyaan pertama',
    zeroStatLeadsHint: 'Nama dan email, di tengah obrolan',
    zeroStatUnansweredHint: 'Belum ada yang perlu diperbaiki',
    heroReadyHeadline: 'Bot-mu siap menjawab',
    heroIndexingHeadline: 'Masih membaca situsmu',
    heroEmptyHeadline: 'Tambahkan sumber untuk memulai',
    heroEmptyBody: 'Ia belum punya apa pun untuk dibaca. Tambahkan sumber dan ia akan siap menjawab dalam hitungan menit.',
    heroIndexingErrorBody:
      'Ada sumber yang perlu diperiksa — tidak akan selesai dengan sendirinya. Cek kartu Pengetahuan untuk lihat apa yang salah, lalu pasang di situsmu begitu sudah diperbaiki.',
    heroIndexingPrefix: 'Ia masih membaca',
    heroIndexingNoSourceFallback: 'sumbermu',
    heroIndexingSuffix: ' — cek lagi dalam beberapa menit, lalu pasang di situsmu.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Ia sudah membaca ${n} halaman`,
    heroReadyFromDomain: ' dari ',
    heroReadyBodySuffix:
      ' dan sudah cukup paham untuk menjawab pertanyaan soal itu. Tinggal satu langkah lagi: pasang di situsmu.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `Cuma ${n} halaman? Tambahkan halaman FAQ, harga, atau kebijakanmu supaya ia punya lebih banyak bahan.`,
    addSourceCta: 'Tambah sumber',
    addSourceHelperText: 'Cuma butuh semenit. Cuplikan embed-nya akan menunggu di sini begitu ada bahan untuk dijawab.',
    pasteBeforeBodyLabel: 'TEMPEL INI SEBELUM </BODY>',
    emailSnippetLink: 'Email-kan cuplikannya ke developer-ku',
    emailSnippetSubject: ({ botName }: { botName: string }) => `Pasang ${botName} di situs kami`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Tempel ini sebelum tag penutup </body>:\n\n${snippet}`,
    whatHappensNextHeading: 'Apa yang terjadi selanjutnya',
    stepVisitorAsksTitle: 'Pengunjung bertanya sesuatu',
    stepVisitorAsksBody: 'Bubble-nya ada di kanan bawah setiap halaman yang dipasangi cuplikannya. Tidak ada lagi yang perlu disiapkan.',
    stepEmailAnswerTitle: 'Kami email-kan jawaban pertamanya ke kamu',
    stepEmailAnswerBody: 'Jadi kamu membaca persis apa yang ia jawab, dalam kata-kata pelangganmu sendiri, sebelum siapa pun.',
    stepUnansweredTitle: 'Yang tidak bisa dijawab masuk ke sini',
    stepUnansweredBody: 'Ia menolak menjawab daripada mengarang. Setiap penolakan jadi satu baris untuk kamu perbaiki.',
    stepAddSourceTitle: 'Tambah sumber',
    stepAddSourceBody: 'Arahkan ke situsmu, atau unggah file dan tempel teks — ia butuh bahan untuk dibaca sebelum bisa menjawab.',
    stepAddSourceVisitorTitle: 'Pengunjung bertanya, kamu dapat email jawabannya',
    countersHeading: 'HITUNGAN DIMULAI SEJAK PENGUNJUNG PERTAMAMU',

    // NeedsYou.tsx
    needsYouHeading: 'Perlu kamu',
    allUnansweredLink: 'Semua belum terjawab →',
    needsYouEmpty: 'Tidak ada yang perlu diperbaiki — bot-mu sudah menjawab semua yang ditanyakan.',
    needsYouIntro: 'Bot-mu bilang tidak tahu. Jawab sekali dan ia akan tahu selamanya.',
    askedSummary: ({ n, time }: { n: number; time: string }) => `Ditanyakan ${n} kali · terakhir ${time}`,
    writeAnswerCta: 'Tulis jawaban',
    cancelCta: 'Batal',
    answerPlaceholder: 'Tulis jawaban yang akan kamu berikan ke pengunjung ini. Bot akan memakainya mulai sekarang.',
    addingCta: 'Menambahkan…',
    addAsQaSourceCta: 'Tambahkan sebagai sumber Q&A',
    saveAnswerError: 'Gagal menyimpan jawaban itu — coba lagi.',
  },
  sources: {
    pageTitle: 'Sumber',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) => `${n} sumber · ${pageCount} halaman · terakhir sinkron ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `Gagal memuat bot ini — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Teks',
    typeFile: 'File',

    colType: 'Jenis',
    colUri: 'URI',
    colStatus: 'Status',
    colRenderedMode: 'Mode rendered',
    colPages: 'Halaman',
    colLastSynced: 'Terakhir sinkron',
    colActions: 'Aksi',

    urlFieldLabel: 'URL halaman',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'URL sitemap',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Judul',
    textTitlePlaceholder: 'Kebijakan pengembalian dana',
    textContentFieldLabel: 'Konten',
    textContentPlaceholder: 'Tempel teks yang ingin kamu buat bot ini tahu…',
    fileFieldLabel: 'File',
    fileDropPrompt: 'Taruh file di sini, atau klik untuk memilih',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · hingga ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `File itu lebih besar dari batas ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'File itu'} tidak didukung — pakai ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Memakai "${name}" — ${count} file lain yang ditaruh diabaikan.`,
    fileDropNotAFile: 'Itu bukan file — seret file dari komputermu.',
    fileTitleFieldLabel: 'Judul (opsional)',
    fileTitlePlaceholder: 'Default-nya nama file',

    addingCta: 'Menambahkan…',
    addSourceCta: 'Tambah sumber',

    emptyTitle: 'Arahkan ke situsmu',
    emptyDescription: 'Tambahkan URL, sitemap, teks, atau file di atas dan kami akan mulai membacanya.',

    reviewPagesLink: 'Tinjau halaman',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) => `${count} halaman terlihat hampir kosong`,

    resyncCta: 'Sinkron ulang',
    deleteCta: 'Hapus',
    cancelCta: 'Batal',
    syncInProgressError: 'Sinkronisasi sedang berjalan — coba lagi nanti',

    deleteConfirmTitle: 'Hapus sumber ini?',
    deleteConfirmDescriptionPrefix: 'Menghapus ',
    deleteConfirmDescriptionSuffix: ' beserta semua yang sudah diindeks. Ini tidak bisa dibatalkan.',

    // RenderedModeToggle.tsx
    renderedModeExplainerAriaLabel: 'Apa itu mode rendered?',
    renderedModeExplainerBody1Bold: 'Mode rendered',
    renderedModeExplainerBody1Rest:
      ' menjalankan setiap halaman di browser sungguhan sebelum membacanya, jadi konten yang baru muncul setelah JavaScript berjalan ikut terbaca.',
    renderedModeExplainerBody2:
      'Biarkan mati untuk situs normal — lebih cepat dan lebih murah. Nyalakan kalau sebuah halaman terlihat kosong bagi crawler (single-page app, dashboard, apa pun yang memuat teksnya setelah halaman terbuka).',
    renderedModeExplainerBody3: 'Trade-off: crawling jadi lebih lambat dan memakai lebih banyak jatah halamanmu per proses.',

    renderedModeLabel: 'Mode rendered',
    modeRendered: 'Rendered',
    modeStandard: 'Standar',
    switchModeTitle: ({ mode }: { mode: string }) => `Beralih ke mode ${mode}?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Ini akan meng-crawl ulang sumbernya dalam mode ${mode}. Halaman yang hasilnya berubah akan diindeks ulang dan yang sudah tidak ada akan dihapus — indeks yang sekarang tetap tersedia sampai crawl barunya selesai.`,
    switchModeCta: ({ mode }: { mode: string }) => `Beralih ke mode ${mode}`,
  },
  sourceDetail: {
    crawledPages: ({ n }: { n: number }) => `Meng-crawl ${n} halaman.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `Meng-crawl ${n} halaman. ${failedCount} halaman gagal dimuat dan dilewati.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `Meng-crawl ${n} halaman sebelum kapasitas crawling kami sendiri habis. Ini sementara di sisi kami — tunggu beberapa menit dan jalankan ulang crawl-nya.`,
    truncatedDeadline: ({ n }: { n: number }) => `Meng-crawl ${n} halaman sebelum crawl ini kena batas waktu kami.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `Meng-crawl ${n} halaman sebelum crawl-nya kehabisan percobaan pada halaman yang tidak mau dimuat.`,
    truncatedPageCap: ({ n }: { n: number }) => `Meng-crawl ${n} halaman — batas maksimum yang diizinkan paketmu saat ini.`,
    remainingAtLeast: ({ n }: { n: number }) => ` Setidaknya ${n} halaman belum terindeks.`,
    remainingNone: ' Beberapa halaman mungkin belum terindeks.',
    crawlFailedGeneric: 'Crawl ini tidak mengindeks satu halaman pun.',

    // SourceDetail.tsx — typeLabel
    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Teks',
    typeFile: 'File',

    languagePrefix: 'Bahasa: ',

    // Facts card
    factType: 'Jenis',
    factRenderedMode: 'Mode rendered',
    factLastSynced: 'Terakhir sinkron',

    resyncCta: 'Sinkron ulang',
    deleteCta: 'Hapus',

    latestCrawlHeading: 'Crawl terbaru',
    neverCrawledYet: 'Sumber ini belum pernah di-crawl.',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) => `Crawl ini berjalan dalam mode ${rendered ? 'rendered' : 'standar'}.`,

    // CrawlDiagnostics
    failedUrlsHeading: 'URL yang gagal',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} total, menampilkan ${shown} pertama`,
    offSiteRedirectsHeading: 'Redirect ke situs lain',
    offSiteRedirectsMoreCaveat: ' — mungkin ada lebih banyak dari yang ditampilkan daftar ini',
    nearEmptyCameBack: ({ n }: { n: number }) => `${n} halaman kembali dengan konten yang hampir kosong.`,

    pagesIndexedHeading: 'Halaman terindeks',
    nearEmptyOnlyToggle: 'Hanya yang hampir kosong',

    // DocumentsTable
    noPagesIndexedYet: 'Belum ada halaman terindeks.',
    colUrl: 'URL',
    colCharacters: 'Karakter',
    colStatus: 'Status',
    nearEmptyBadge: 'hampir kosong',

    // indexStateLabel
    indexStateIndexed: 'Terindeks',
    indexStateIndexing: 'Mengindeks…',
    indexStateFailed: 'Gagal',

    // DocumentContentViewer
    fileNoMarkdownExplainer:
      'Tersimpan sebagai file yang diunggah itu sendiri — kami menyimpan aslinya, bukan salinan teks terpisah, jadi tidak ada yang bisa dipratinjau di sini. File-nya sudah terindeks dan bot menjawab darinya.',
    nothingStoredForDocument: 'Tidak ada yang tersimpan untuk dokumen ini.',

    storedContentHeading: 'Konten tersimpan',
    nothingStoredYet: 'Belum ada yang tersimpan.',

    deleteDialogTitle: 'Hapus sumber ini?',
    deleteDialogDescriptionPrefix: 'Menghapus ',
    deleteDialogDescriptionSuffix: ' beserta semua yang sudah diindeks. Ini tidak bisa dibatalkan.',
    cancelCta: 'Batal',
  },
  conversations: {
    today: 'Hari ini',
    yesterday: 'Kemarin',
    yourTesting: 'Percobaanmu',
    visitor: 'Pengunjung',
    playgroundTag: 'Playground',
    noMessageFallback: '(tidak ada pesan)',
    leadCapturedTag: 'Lead tertangkap',
    hasUnansweredTag: 'Ada yang belum terjawab',
    unansweredChipLabel: 'Belum terjawab',
    loadingMore: 'Memuat lagi…',
    loadMore: 'Muat lagi',
    selectConversationPrompt: 'Pilih percakapan untuk melihat transkripnya.',
    backToConversations: '← Kembali ke percakapan',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) => `Dimulai ${time} · ${n} pesan`,
    deleteConversationAriaLabel: 'Hapus percakapan ini',
    leadSubmittedTag: '✓ Lead terkirim',
    cancelCta: 'Batal',
    deletingCta: 'Menghapus…',

    pageTitle: 'Percakapan',
    headerSubtitle: ({ n }: { n: number }) => `${n} percakapan · terbaru dulu`,
    emptyTitle: 'Menunggu pengunjung pertamamu',
    emptyDescription: 'Percakapan akan muncul di sini begitu ada yang mengobrol dengan bot-mu.',

    deleteAllCta: 'Hapus semua percakapan',
    deleteDialogTitle: 'Hapus percakapan ini?',
    deleteDialogDescription:
      'Menghapus permanen transkrip ini beserta pesan-pesannya. Lead yang tertangkap di dalamnya tetap ada di tab Lead. Ini tidak bisa dibatalkan.',
    deleteCta: 'Hapus percakapan',

    deleteAllDialogTitle: 'Hapus semua percakapan?',
    deleteAllDescriptionPrefix:
      'Menghapus permanen setiap percakapan dan pesan untuk bot ini. Lead yang tertangkap tetap ada di tab Lead. Ini tidak bisa dibatalkan. Ketik ',
    deleteAllDescriptionSuffix: ' untuk konfirmasi.',
    confirmBotNameAriaLabel: 'Konfirmasi nama bot',
  },
  leads: {
    filterNew: 'Baru',
    filterContacted: 'Dihubungi',
    filterArchived: 'Diarsipkan',
    filterAll: 'Semua',
    actionMarkNew: 'Tandai baru',
    actionMarkContacted: 'Tandai dihubungi',
    actionArchive: 'Arsipkan',
    statusNew: 'baru',
    statusContacted: 'dihubungi',
    statusArchived: 'diarsipkan',

    filterByStatusAriaLabel: 'Filter berdasarkan status',
    pageTitle: 'Lead',
    exportingCta: 'Mengekspor…',
    exportCsvCta: 'Ekspor CSV',
    exportCsvError: 'Gagal mengekspor lead — coba lagi.',

    headerSubtitleAll: ({ n }: { n: number }) => `${n} lead tertangkap · terbaru dulu`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} lead ${filterLabel} · terbaru dulu`,

    deleteAllCta: 'Hapus semua lead',
    bulkActionsAriaLabel: 'Aksi massal',
    selectedCount: ({ n }: { n: number }) => `${n} dipilih`,
    bulkCapMessage: ({ max }: { max: number }) => `Pilih hingga ${max} lead sekaligus`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} diperbarui`,
    bulkUpdateError: 'Gagal memperbarui lead itu. Coba lagi.',

    emptyTitle: 'Menunggu lead pertamamu',
    emptyDescription: 'Lead yang ditangkap widget chat bot-mu akan muncul di sini.',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Tidak ada lead ${filterLabel}`,
    noFilterResultsDescription: 'Belum ada apa pun di filter ini.',

    selectAllAriaLabel: 'Pilih semua lead',
    colName: 'Nama',
    colContact: 'Kontak',
    colIntent: 'Niat',
    colStatus: 'Status',
    colCaptured: 'Ditangkap',
    colActions: 'Aksi',

    noEmailFallback: '(tidak ada email)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Pilih lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Hapus lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'Lihat chat',
    loadingMoreCta: 'Memuat lagi…',
    loadMoreCta: 'Muat lagi',

    cancelCta: 'Batal',
    deletingCta: 'Menghapus…',

    deleteDialogTitle: 'Hapus lead ini?',
    deleteDescriptionPrefix: 'Menghapus permanen ',
    deleteDescriptionSuffix: '. Ini tidak bisa dibatalkan.',
    noContactFallback: 'lead ini',
    deleteCta: 'Hapus lead',

    deleteAllDialogTitle: 'Hapus semua lead?',
    deleteAllDescriptionPrefix: 'Menghapus permanen setiap lead yang tertangkap untuk bot ini. Ini tidak bisa dibatalkan. Ketik ',
    deleteAllDescriptionSuffix: ' untuk konfirmasi.',
    confirmBotNameAriaLabel: 'Konfirmasi nama bot',
  },
  unanswered: {
    tabOpen: 'Terbuka',
    tabDismissed: 'Diabaikan',
    tabAnswered: 'Terjawab',
    filterByStatusAriaLabel: 'Filter berdasarkan status',

    pageTitle: 'Belum Terjawab',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) => `${n} pertanyaan ${verb}`,
    subtitleVerbOpen: 'menunggu jawaban',
    subtitleVerbDismissed: 'diabaikan',
    subtitleVerbAnswered: 'terjawab',

    openTabIntro:
      'Pertanyaan yang tidak bisa dijawab bot-mu, dikelompokkan. Tulis jawabannya sekali — itu jadi sumber Q&A dan bot-nya akan tahu selamanya.',

    emptyTitleOpen: 'Tidak ada yang terbuka — bot-mu sudah menjawab semua yang ditanyakan.',
    emptyTitleDismissed: 'Belum ada yang diabaikan.',
    emptyTitleAnswered: 'Belum ada pertanyaan terjawab.',

    askedOnce: 'Ditanyakan sekali',
    askedTimes: ({ n }: { n: number }) => `Ditanyakan ${n} kali`,

    viewSourceCta: 'Lihat sumber',
    sourceRemoved: 'sumber dihapus',
    viewChatCta: 'Lihat chat',
    chatDeleted: 'chat dihapus',

    dismissCta: 'Abaikan',
    reopenCta: 'Buka lagi',
    writeAnswerCta: 'Tulis jawaban',
    cancelCta: 'Batal',
    answerPlaceholder: 'Tulis jawaban yang akan kamu berikan ke pengunjung ini. Bot akan memakainya mulai sekarang.',
    addingCta: 'Menambahkan…',
    addAsQaSourceCta: 'Tambahkan sebagai sumber Q&A',
    answerAddedNotice: 'Ditambahkan sebagai sumber Q&A — bot akan memakainya mulai percakapan berikutnya.',
    saveAnswerError: 'Gagal menyimpan jawaban itu — coba lagi.',
  },
  appearance: {
    pageTitle: 'Tampilan',
    pageSubtitle: 'Bagaimana widget terlihat dan menyapa pengunjung di situsmu',

    colorLabel: 'Warna',
    colorHexAriaLabel: 'Nilai hex warna',
    botNameLabel: 'Nama bot',
    botNamePlaceholder: 'Support Acme',
    welcomeMessageLabel: 'Pesan sambutan',
    welcomePlaceholder: 'Hai! Ada yang bisa saya bantu hari ini?',

    quickQuestionsLabel: 'Pertanyaan cepat',
    addQuestionCta: 'Tambah pertanyaan',
    quickQuestionsHint: 'Opsional — muncul sebagai chip yang bisa diketuk pengunjung sebelum mereka mengetik apa pun.',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Pertanyaan cepat ${n}`,
    quickQuestionPlaceholder: 'Jam operasional bisnismu berapa?',
    removeCta: 'Hapus',

    positionLabel: 'Posisi launcher',
    positionBottomRight: 'Kanan bawah',
    positionBottomLeft: 'Kiri bawah',

    languageLabel: 'Bahasa',
    autoLanguageLabel: 'Ikuti bahasa browser pengunjung',

    badgeLabel: 'Lencana "Powered by Embent"',
    badgeHint: 'Menampilkan tautan kecil "Powered by Embent" di footer widget.',
    badgeSwitchAriaLabel: 'Tampilkan lencana "Powered by Embent"',
    badgeLoadError: 'Gagal memuat — refresh',
    freeBadgeNote: ({ plans }: { plans: string }) => `Upgrade ke ${plans} menghapus keharusan ini.`,

    saveCta: 'Simpan',
    savingCta: 'Menyimpan…',
    savedLabel: 'Tersimpan',

    livePreviewHeading: 'Pratinjau langsung',
    previewCaption: 'Hanya tombol launcher dan panelnya yang dirender di sini — halaman di sekitarnya sengaja dikosongkan.',
    devHintPrefix: 'Kalau tidak ada yang muncul, bundle widget-nya belum dibangun secara lokal: jalankan',
    devHintIn: 'di',
    devHintAndCopy: 'dan salin',
    devHintTo: 'ke',

    // WidgetPreview.tsx
    widgetPreviewTitle: 'Pratinjau widget',
  },
  botSettings: {
    pageTitle: 'Pengaturan',
    pageSubtitle: 'Domain yang diizinkan, penangkapan lead, dan cara bot memperkenalkan dirinya',

    leadCaptureOffLabel: 'Mati',
    leadCaptureOffBlurb: 'Bot tidak pernah meminta detail kontak dari pengunjung.',
    leadCaptureOnIntentLabel: 'Sesuai niat',
    leadCaptureOnIntentBlurb: 'Bot meminta email atau nomor telepon hanya kalau tidak bisa menjawab atau pengunjung ingin tindak lanjut.',
    leadCaptureAlwaysLabel: 'Selalu',
    leadCaptureAlwaysBlurb: 'Bot meminta detail kontak sekali, persis setelah pertukaran pertama dalam percakapan.',
    leadCapturePreChatLabel: 'Form sebelum chat',
    leadCapturePreChatBlurb:
      'Pengunjung mengisi form kontak singkat (nama, email, telepon opsional) sebelum chat dimulai. Bot tidak akan bertanya lagi di dalam chat.',

    leadLevelSubtleLabel: 'Halus',
    leadLevelSubtleBlurb: 'Hanya menawarkan tindak lanjut kalau pengunjung minta dihubungi atau ingin penawaran harga.',
    leadLevelBalancedLabel: 'Seimbang (disarankan)',
    leadLevelBalancedBlurb:
      'Menawarkan sekali saat pengunjung menunjukkan sinyal membeli yang nyata — bertanya cara mulai, ingin demo, atau menyebut situsnya sendiri.',
    leadLevelProactiveLabel: 'Proaktif',
    leadLevelProactiveBlurb:
      'Menawarkan kapan pun pengunjung tampak sedang mempertimbangkan, dan bertanya sekali lagi nanti kalau tawaran pertama tidak dijawab.',

    domainRequiredError: 'Wajib diisi — hapus barisnya kalau mau dikosongkan',
    domainBareHostnameError: 'Hanya hostname polos, mis. "contoh.com" (tanpa skema atau path)',
    domainTooLongError: 'Terlalu panjang',

    aboutLabel: 'Tentang bot ini',
    aboutHint:
      'Perkenalan singkat yang selalu diketahui bot — siapa dia, siapa yang dilayaninya — jadi pertanyaan seperti ' +
      '"kamu siapa?" langsung terjawab tanpa perlu pencarian.',
    aboutPlaceholder: 'Kami adalah asisten support Acme, membantu pelanggan memasang dan mengatasi masalah Acme Widgets.',

    allowedDomainsLabel: 'Domain yang diizinkan',
    addDomainCta: 'Tambah domain',
    allowedDomainsHint:
      'Situs yang diizinkan menyematkan widget bot ini. Kosongkan untuk mengizinkan semua situs — disarankan ' +
      'untuk membatasi ini ke domainmu sendiri sebelum go-live.',
    domainAriaLabel: ({ n }: { n: number }) => `Domain yang diizinkan ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Hapus',

    leadCaptureLabel: 'Penangkapan lead',
    leadLevelLabel: 'Seberapa proaktif?',

    saveCta: 'Simpan',
    savingCta: 'Menyimpan…',
    savedLabel: 'Tersimpan',

    embedSnippetHeading: 'Cuplikan embed',
    embedPastePrefix: 'Tempel ini sebelum tag penutup',
    embedPasteSuffix: 'di situsmu.',

    // EmbedSnippet.tsx
    embedCopyCta: 'Salin',
    embedCopiedCard: 'Tersalin!',
    embedCopiedCheck: '✓ Tersalin',

    dangerZoneHeading: 'Zona berbahaya',
    dangerZoneDescription: 'Menghapus bot ini, sumbernya, percakapan, lead, dan indeks pencariannya. Ini tidak bisa dibatalkan.',
    deleteBotCta: 'Hapus bot ini',
    deleteBotDialogTitle: ({ name }: { name: string }) => `Hapus "${name}"?`,
    dangerZoneDialogDescriptionPrefix:
      'Menghapus bot ini, sumbernya, percakapan, lead, dan indeks pencariannya. Ini tidak bisa dibatalkan. Ketik ',
    dangerZoneDialogDescriptionSuffix: ' untuk konfirmasi.',
    confirmBotNameAriaLabel: 'Konfirmasi nama bot',
    cancelCta: 'Batal',
    deletingCta: 'Menghapus…',
  },
  notifications: {
    heading: 'Notifikasi',
    subheading: 'Email dikirim ke alamat akun masing-masing orang. Aktivitas di playground tidak pernah memicu email.',
    noMembers: 'Belum ada anggota.',
    saveError: 'Gagal menyimpan pengaturan itu. Coba lagi.',

    roleOwner: 'Pemilik',
    roleAdmin: 'Admin',
    roleMember: 'Anggota',
    cadenceOff: 'Mati',
    cadenceDaily: 'Harian',
    cadenceWeekly: 'Mingguan',

    colMember: 'Anggota',
    colNewLead: 'Lead baru',
    colUnansweredDigest: 'Ringkasan belum terjawab',
    colCrawlFailed: 'Crawl gagal',
    colWeeklySummary: 'Ringkasan mingguan',

    newLeadAriaLabel: ({ email }: { email: string }) => `Email lead baru untuk ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `Email ringkasan belum terjawab untuk ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `Email crawl gagal untuk ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `Email ringkasan mingguan untuk ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: 'Mengobrol dengan bot-mu seperti pengunjung — tidak ada apa pun di sini yang sampai ke situsmu.',
    retrievalDebugLabel: 'Debug retrieval',
    hideDebugCta: 'Sembunyikan debug',
    newSessionCta: 'Sesi baru',
    emptyStateText: 'Kirim pesan untuk menguji bagaimana bot ini menjawab, berdasar pada sumbernya.',
    inputPlaceholder: 'Ajukan pertanyaan ke bot ini…',
    stopCta: 'Berhenti',
    sendCta: 'Kirim',
    debugEmptyText: 'Ajukan pertanyaan untuk melihat apa yang diambilnya',
    genericError: 'Ada yang tidak beres.',
  },
  billing: {
    pageTitle: 'Tagihan',
    openingCta: 'Membuka…',
    manageBillingCta: 'Kelola tagihan',

    memberGateTitle: 'Tagihan dikelola oleh pemilik workspace atau admin.',
    memberGateDescription: 'Minta pemilik atau admin untuk mengubah paket workspace ini.',

    nowOnPlan: ({ name }: { name: string }) => `Kamu sekarang di paket ${name}.`,
    finishingUp: 'Kami sedang menyelesaikan ini — refresh sesaat lagi kalau paketmu belum berubah.',
    confirmingWithPolar: 'Perubahan diterima — mengonfirmasi dengan Polar…',

    internalWorkspaceLabel: 'Workspace internal',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / bulan`,
    internalWorkspaceInline: 'Tanpa batas, tagihan tidak berlaku.',
    internalWorkspaceHeading: 'Workspace internal — tanpa batas, tagihan tidak berlaku.',
    internalWorkspaceBody:
      'Setiap batas paket dilewati dan tidak ada yang perlu di-upgrade atau di-checkout di sini. Pemakaian ' +
      'tetap dicatat di atas untuk visibilitas, tapi tidak pernah dikirim ke Polar — workspace ini ' +
      'tidak punya identitas tagihan.',

    unlimitedWord: 'tanpa batas',
    unlimitedParens: '(tanpa batas)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: 'Workspace internal · tanpa batas · tagihan tidak berlaku',
    capsSuffix: ({
      bots,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} bot · ${messages} pesan · ${pages} halaman`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Gratis selamanya · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) => `Diperpanjang ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Berakhir di akhir periode',
    planChangesPausedNotice: 'Perubahan paket dijeda sampai periode saat ini berakhir.',
    planChangesPausedReason: 'Langganan ini diatur untuk berakhir di akhir periode — lanjutkan dulu untuk bisa ganti paket.',

    redirectingCta: 'Mengalihkan…',
    switchingCta: 'Beralih…',
    upgradeToCta: ({ name }: { name: string }) => `Upgrade ke ${name}`,

    messagesThisMonthLabel: 'Pesan bulan ini',
    pagesIndexedLabel: 'Halaman terindeks',
    messageNoun: 'pesan',
    pageNoun: 'halaman',
    messagesUnit: ({ n }: { n: string }) => `${n} pesan`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) => `${pct}% dari batas ${noun}mu`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) => ` — ${name} menaikkannya jadi ${unit}.`,
    limitReached: ' tercapai.',

    cancelSubscriptionCta: 'Batalkan langganan',
    cancelingCta: 'Membatalkan…',
    cancelDialogTitle: 'Batalkan langganan?',
    yourCurrentPlanFallback: 'paketmu saat ini',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Bot-botmu tetap berjalan di ${planName} sampai akhir periode tagihan ini${
        renewsDate ? ` (${renewsDate})` : ''
      } — lalu workspace ini turun ke Free. Ini tidak pernah terjadi seketika.`,
    keepSubscriptionCta: 'Tetap berlangganan',

    changePlanFallbackTitle: 'Ganti paket?',
    keepCurrentPlanCta: 'Tetap di paket saat ini',
    confirmFallbackCta: 'Konfirmasi',

    checkoutErrorFallback: 'Ada yang tidak beres saat memulai checkout.',
    portalErrorFallback: 'Ada yang tidak beres saat membuka pengelolaan tagihan.',
    cancelErrorFallback: 'Ada yang tidak beres saat membatalkan langgananmu.',

    // PlanCard
    currentPlanBadge: 'Paket saat ini',
    botsUnit: ({ n }: { n: number }) => `${n} bot`,
    seatsUnit: ({ n }: { n: number }) => `${n} kursi`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} pesan / bln`,
    pagesUnit: ({ n }: { n: string }) => `${n} halaman`,
    badgeRequiredText: 'Lencana "Powered by Embent"',
    badgeOptionalText: 'Lencana "Powered by Embent" opsional',
    upgradeCta: 'Upgrade',
    switchToCta: ({ name }: { name: string }) => `Beralih ke ${name}`,

    flatPricingNote:
      'Harga flat, tanpa hitung-hitungan per kursi, tanpa biaya kejutan berlebih — kami memberi peringatan di 80% ' +
      'dan baru menjeda jawaban baru setelah lewat 110%, dan tidak pernah memutus percakapan di tengah jawaban.',

    planChangeConfirm: {
      verbUpgrade: 'Upgrade',
      verbSwitch: 'Beralih',
      title: ({ verb, name }: { verb: string; name: string }) => `${verb} ke ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} sekarang`,
      continueToPolarCta: 'Lanjut ke Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Kamu akan diarahkan ke Polar untuk memulai langganan ${name} ($${price}/bln) — Polar akan ` +
        'menagihmu di sana untuk mengaktifkannya.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Ini langsung mengganti paketmu — bot-botmu langsung memakai batas ${name} ` +
        `($${price}/bln) saat ini juga. Polar tidak akan menagihmu hari ini: selisih prorata-nya ` +
        'diterapkan ke invoice berikutnya.',
    },
  },
  workspace: {
    pageTitle: 'Pengaturan workspace',
    loadErrorMessage: 'Gagal memuat workspace ini. Muat ulang halaman untuk mencoba lagi.',
    notAllowedTitle: 'Hanya pemilik dan admin yang bisa mengubah pengaturan workspace.',
    notAllowedDescription: 'Minta pemilik atau admin workspace ini untuk melakukan perubahan.',

    nameLabel: 'Nama workspace',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} karakter. Terlihat oleh semua orang di workspace ini dan di email undangan.`,

    uploadLogoCta: 'Unggah logo',
    removeLogoCta: 'Hapus',
    logoHint: 'Bentuk persegi paling cocok. PNG, JPEG, WebP, atau GIF, hingga 2 MB.',
    logoNotConfiguredError: 'Unggah logo belum dikonfigurasi di environment ini.',
    uploadLogoErrorFallback: 'Gagal mengunggah logo. Coba lagi.',
    removeLogoErrorFallback: 'Gagal menghapus logo. Coba lagi.',

    saveNameErrorFallback: 'Gagal menyimpan nama itu. Coba lagi.',
    saveCta: 'Simpan',
    savedLabel: 'Tersimpan',

    // LogoCropDialog.tsx
    cropDialogTitle: 'Pangkas logomu',
    cropDialogDescription: 'Seret untuk memposisikan, zoom untuk menyesuaikan. Tersimpan sebagai persegi 256×256.',
    zoomLabel: 'Perbesar',
    cropErrorFallback: 'Gagal memangkas gambar ini. Coba file lain.',
    cancelCta: 'Batal',
    useThisLogoCta: 'Pakai logo ini',
  },
  members: {
    pageTitle: 'Anggota',
    membersOnlyTitle: 'Area khusus anggota',
    membersOnlyDescription: 'Minta pemilik workspace atau admin untuk mengelola anggota.',
    unlimitedCapWord: 'Tanpa batas',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} dari ${cap} kursi terpakai${pending > 0 ? ` (${pending} diundang)` : ''}`,
    inviteMemberCta: 'Undang anggota',

    roleOwner: 'Pemilik',
    roleAdmin: 'Admin',
    roleMember: 'Anggota',
    roleOptionMember: 'Anggota',
    roleOptionAdmin: 'Admin',

    colName: 'Nama',
    colEmail: 'Email',
    colRole: 'Peran',
    leaveWorkspaceCta: 'Keluar dari workspace',
    removeCta: 'Hapus',

    pendingInvitesHeading: 'Undangan tertunda',
    noPendingInvites: 'Tidak ada undangan tertunda.',
    inviteColEmail: 'Email',
    inviteColRole: 'Peran',
    inviteColExpires: 'Kedaluwarsa',
    revokingCta: 'Mencabut…',
    revokeCta: 'Cabut',

    cancelCta: 'Batal',
    removingCta: 'Menghapus…',
    leaveDialogTitle: 'Keluar dari workspace ini?',
    removeDialogTitle: ({ name }: { name: string }) => `Hapus ${name}?`,
    leaveDialogDescription: 'Kamu akan langsung kehilangan akses — pemilik atau admin bisa mengundangmu lagi nanti.',
    removeDialogDescription: ({ name }: { name: string }) => `${name} langsung kehilangan akses ke workspace ini.`,

    emailLabel: 'Email',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Peran',
    inviteErrorFallback: 'Ada yang tidak beres saat mengirim undangan ini.',
    sendingInviteCta: 'Mengirim…',
    sendInviteCta: 'Kirim undangan',

    roleChangeErrorFallback: 'Gagal mengubah peran ini.',
    removeErrorFallback: 'Gagal menghapus anggota ini.',
    revokeErrorFallback: 'Gagal mencabut undangan ini.',
  },
  account: {
    pageTitle: 'Akun',
    loadSignInMethodsError: 'Gagal memuat metode masuk',
    emailPasswordLabel: 'Email & kata sandi',
    googleLabel: 'Google',

    savingCta: 'Menyimpan…',

    profileHeading: 'Profil',
    displayNameLabel: 'Nama tampilan',
    namePlaceholder: 'Namamu',
    emailLabel: 'Email',
    saveNameErrorFallback: 'Ada yang tidak beres saat menyimpan namamu.',
    saveCta: 'Simpan',
    savedLabel: 'Tersimpan',

    passwordHeading: 'Kata sandi',
    passwordMismatchError: 'Kata sandi baru dan konfirmasinya tidak cocok.',
    currentPasswordLabel: 'Kata sandi saat ini',
    newPasswordLabel: 'Kata sandi baru',
    confirmNewPasswordLabel: 'Konfirmasi kata sandi baru',
    changePasswordErrorFallback: 'Ada yang tidak beres saat mengganti kata sandimu.',
    changePasswordCta: 'Ganti kata sandi',
    passwordChangedLabel: 'Kata sandi diganti',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'Gagal mengimpersonasi pengguna ini.',
    lookupFailedFallback: 'Gagal mencari pengguna ini.',
    userNotFound: 'Akun pengguna tidak ditemukan.',
    noOwnerToView: 'Workspace ini tidak punya pemilik untuk dilihat sebagai.',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Halaman ${page} dari ${totalPages} · ${totalCount} total`,
      prevCta: 'Sebelumnya',
      nextCta: 'Berikutnya',
    },
    workspaces: {
      // CrawlSignalPanel
      crawlSignalRenderedLabel: 'Bergantung pada crawling mode rendered',
      crawlSignalSourcesAcrossBots: ({
        botCountLabel,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) => `sumber di ${botCountLabel} bot`,
      crawlSignalRenderedFootnote:
        'Seberapa besar armada bergantung pada Cloudflare Browser Rendering — kapasitas yang kita dapat di Workers Free plan hari ini, bukan Paid.',
      crawlSignalCapacityLabel: 'Kena batas kapasitas rendering kami sendiri (7h)',
      // `n` only drove English's singular/plural branch; Indonesian has none, so the param is
      // unused but kept in the signature to match `en.ts`'s function shape exactly.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `sumber`,
      crawlSignalCapacityFootnote:
        'Sumber unik, hanya kapasitas kami sendiri — bukan batas paket pelanggan. Proses yang mati atau job basi yang dibersihkan tidak meninggalkan jejak — ini angka minimum. Kalau terus naik, saatnya Workers Paid ($5/bln).',

      // AdminWorkspaces.tsx
      emptyText: 'Belum ada workspace.',
      internalBadge: 'INTERNAL',
      cancelsAtPeriodEndHint: 'berakhir di akhir periode',
      colName: 'Nama',
      colOwner: 'Pemilik',
      colPlan: 'Paket',
      colBots: 'Bot',
      colMembers: 'Anggota',
      colMsgsPerMo: 'Psn / bln',
      colPages: 'Halaman',
      colCreated: 'Dibuat',
      colActions: 'Aksi',
      noOwnerTitle: 'Workspace ini tidak punya pemilik',
      viewAsCta: 'Lihat sebagai',
      switchingCta: 'Beralih…',
      viewAsErrorFallback: 'Gagal',

      // AdminWorkspaceDetail.tsx
      backToWorkspaces: '← Kembali ke Workspace',
      ownerLabel: 'Pemilik: ',
      bannedBadge: 'diblokir',
      createdLabel: ({ date }: { date: string }) => `Dibuat ${date}`,
      usageHeading: 'Pemakaian',
      messagesThisMonthLabel: 'Pesan bulan ini',
      pagesIndexedLabel: 'Halaman terindeks',
      botsLabel: 'Bot',
      internalUsageNote: 'Workspace internal — tidak ada batas yang berlaku; pemakaian tetap dicatat di atas.',
      botsHeading: 'Bot',
      noBotsYet: 'Belum ada bot.',
      colBotName: 'Nama',
      colPublicId: 'Public ID',
      colStatus: 'Status',
      colBotCreated: 'Dibuat',
      membersHeading: 'Anggota',
      noMembersYet: 'Belum ada anggota.',
      colEmail: 'Email',
      colMemberName: 'Nama',
      colRole: 'Peran',
      colJoined: 'Bergabung',
      memberRoleLabel: {
        owner: 'Pemilik',
        admin: 'Admin',
        member: 'Anggota',
      },
      subscriptionHeading: 'Langganan',
      statusLabel: 'Status: ',
      periodEndLabel: 'Akhir periode: ',
      cancelsAtPeriodEndLabel: 'Berakhir di akhir periode: ',
      yesLabel: 'Ya',
      noLabel: 'Tidak',
      polarSubscriptionIdLabel: 'ID langganan Polar: ',
      manageInPolarLink: 'Kelola di Polar ↗',
      noSubscriptionText: 'Tidak ada langganan — workspace ini di paket gratis.',
      dangerZoneHeading: 'Zona berbahaya',
      internalToggleLabel: 'Workspace internal',
      internalToggleHint: 'Tanpa batas, tanpa tagihan — pemakaian tetap dicatat tapi tidak pernah dikirim ke Polar.',
      banOwnerLabel: 'Blokir pemilik',
      banOwnerHint: 'Pengguna yang diblokir tidak bisa masuk. Widget/API publik tidak terpengaruh.',
      unbanCta: 'Buka blokir',
      unbanningCta: 'Membuka blokir…',
      banCta: 'Blokir',
      demoteBeforeBanTitle: 'Turunkan admin ini jadi user dulu sebelum memblokirnya.',
      roleLabel: 'Peran',
      roleHint: 'Admin bisa mengakses setiap layar admin internal.',
      cannotChangeOwnRoleTitle: 'Kamu tidak bisa mengubah perananmu sendiri.',
      cancelCta: 'Batal',
      savingCta: 'Menyimpan…',
      confirmCta: 'Konfirmasi',
      markInternalDialogTitle: 'Tandai workspace ini sebagai internal?',
      removeInternalDialogTitle: 'Hapus status internal?',
      markInternalDialogDescription:
        'Workspace ini akan melewati setiap batas paket (bot, pesan, halaman) dan tidak akan pernah ditagih. Pemakaian tetap dicatat untuk visibilitas, tapi pengiriman pemakaian Polar tiap malam melewatkannya — tidak ada identitas tagihan untuk dikirimkan.',
      removeInternalDialogDescription: 'Workspace ini kembali ke penegakan paket dan tagihan normal (kalau punya langganan aktif).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `Blokir ${email}?`,
      banDialogDescription: 'Ini langsung mengeluarkan mereka dan mencegah masuk lagi sampai diblokir dibuka.',
      banReasonLabel: 'Alasan (opsional)',
      banReasonPlaceholder: 'Kenapa akun ini diblokir?',
      banningCta: 'Memblokir…',
      banUserCta: 'Blokir pengguna',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `Ubah peran jadi ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} akan mendapat akses ke setiap layar admin internal.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} akan kehilangan akses ke layar admin internal.`,
      updateWorkspaceErrorFallback: 'Gagal memperbarui workspace ini.',
      banUserErrorFallback: 'Gagal memblokir pengguna ini.',
      unbanUserErrorFallback: 'Gagal membuka blokir pengguna ini.',
      changeRoleErrorFallback: 'Gagal mengubah peran pengguna ini.',
    },
    bots: {
      // AdminBots.tsx
      colName: 'Nama',
      colPublicId: 'Public ID',
      colWorkspace: 'Workspace',
      colStatus: 'Status',
      colSources: 'Sumber',
      colLastCrawl: 'Crawl terakhir',
      colInstanceId: 'Instance ID',
      noBotsYet: 'Belum ada bot.',

      // AdminBotDetail.tsx
      backToBots: '← Kembali ke Bot',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instance: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Dibuat ${date}`,
      openOwnerViewCta: 'Buka tampilan pemilik',
      openingCta: 'Membuka…',
      openOwnerViewErrorFallback: 'Gagal membuka tampilan pemilik.',
      sourcesHeading: 'Sumber',
      noSourcesYet: 'Belum ada sumber.',
      colUrl: 'URL',
      colType: 'Jenis',
      colSourceStatus: 'Status',
      colPages: 'Halaman',
      colLastCrawled: 'Terakhir di-crawl',
      colError: 'Galat',
      recentJobsHeading: 'Tugas terbaru',
      noJobsLinked: 'Belum ada tugas yang terkait dengan bot ini.',
      colKind: 'Jenis',
      colJobStatus: 'Status',
      colAttempts: 'Percobaan',
      colUpdated: 'Diperbarui',
      colPayload: 'Payload',
      recentJobsFootnote:
        'Tugas terbaru dicocokkan sebaik mungkin lewat payload masing-masing — tugas yang tidak membawa id bot ini atau salah satu id sumbernya tidak akan muncul di sini meski sebenarnya menyentuh bot ini secara tidak langsung.',
    },
    jobs: {
      tabLabel: {
        all: 'Semua',
        running: 'Berjalan',
        failed: 'Gagal',
      },
      noFailedJobs: 'Tidak ada tugas gagal — antreannya sehat.',
      noJobsToShow: 'Tidak ada tugas untuk ditampilkan.',
      colKind: 'Jenis',
      colStatus: 'Status',
      colPayload: 'Payload',
      colAttempts: 'Percobaan',
      colCreated: 'Dibuat',
      colUpdated: 'Diperbarui',
      colActions: 'Aksi',
      retryCta: 'Coba lagi',
      retryingCta: 'Mencoba lagi…',
      cancelCta: 'Batal',
      cancellingCta: 'Membatalkan…',
      cancelJobCta: 'Batalkan tugas',
      keepJobCta: 'Pertahankan tugas',
      cancelDialogTitle: 'Batalkan tugas ini?',
      retryJobErrorFallback: 'Gagal mencoba ulang tugas ini.',
      cancelJobErrorFallback: 'Gagal membatalkan tugas ini.',
      cancelDialogDescriptionListRow:
        'Ini menghapus baris tugas yang tertunda — tidak akan pernah berjalan. Hanya tugas yang tertunda yang bisa dibatalkan dengan cara ini; handler dari tugas yang sedang berjalan mungkin sudah bekerja di tengah jalan.',

      // AdminJobDetail.tsx
      backToJobs: '← Kembali ke Tugas',
      jobIdLabel: 'Job ID',
      attemptsLabel: 'Percobaan',
      createdLabel: 'Dibuat',
      updatedLabel: 'Diperbarui',
      runAfterLabel: 'Jalankan setelah',
      errorHeading: 'Galat',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'Ini menghapus baris tugas yang tertunda — tidak akan pernah berjalan.',
    },
    posts: {
      description: 'Postingan blog, draf dan yang dipublikasikan. Mengedit di sini langsung live di embent.com dalam semenit sejak dipublikasikan.',
      newPostCta: 'Postingan baru',
      emptyText: 'Belum ada postingan — buat yang pertama.',
      colTitle: 'Judul',
      colStatus: 'Status',
      colAuthor: 'Penulis',
      colPublished: 'Dipublikasikan',
      colUpdated: 'Diperbarui',
      scheduledLabel: ({ date }: { date: string }) => `terjadwal · ${date}`,
    },
    postEditor: {
      // Header
      backToPosts: '← Postingan',
      newPostTitle: 'Postingan baru',
      editPostTitleFallback: 'Edit postingan',

      // Post fields
      titleLabel: 'Judul',
      titlePlaceholder: 'Bagaimana Embent menjawab pertanyaan support',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Ringkasan',
      excerptPlaceholder: 'Satu atau dua kalimat — muncul di /blog dan dipakai sebagai meta description.',
      tagsLabel: 'Tag',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Dipisahkan koma.',
      coverUrlLabel: 'URL gambar sampul',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      // Author picker
      authorLabel: 'Penulis',
      editAuthorCta: 'Edit penulis',
      newAuthorCta: '+ Penulis baru',
      authorSelectPlaceholder: 'Pilih penulis',
      noAuthorsYet: 'Belum ada penulis — tambahkan satu lewat "+ Penulis baru" di atas.',

      // Publish / visibility
      visibilityScheduledText: ({ date }: { date: string }) => `Live ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Dipublikasikan ${date}`,
      visibilityDraftText: 'Belum dipublikasikan',
      publishDateLabel: 'Tanggal publikasi',
      publishDateHintPrefix: 'Waktu dalam ',
      publishDateHintSuffix:
        ' — jam browser ini. Kosongkan untuk langsung live begitu kamu publikasikan. Atur waktu di masa depan dan postingannya tetap tersembunyi sampai saat itu tiba — ia menghilang dari blog, hitungan tag-nya, sitemap, dan feed RSS sampai jamnya tiba. Tidak perlu ada yang berjalan supaya ia muncul.',

      // Body + preview
      bodyLabel: 'Isi (Markdown)',
      livePreviewLabel: 'Pratinjau langsung',

      // Validation
      missingFieldLabel: {
        title: 'judul',
        slug: 'slug',
        excerpt: 'ringkasan',
        body: 'isi',
        author: 'penulis',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Sebelum menyimpan, tambahkan: ${fields}.`,

      // Save / publish actions
      savingCta: 'Menyimpan…',
      saveChangesCta: 'Simpan perubahan',
      saveDraftCta: 'Simpan draf',
      unpublishingCta: 'Membatalkan publikasi…',
      schedulingCta: 'Menjadwalkan…',
      publishingCta: 'Mempublikasikan…',
      unpublishCta: 'Batalkan publikasi',
      scheduleCta: 'Jadwalkan',
      publishCta: 'Publikasikan',
      savedFlash: 'Tersimpan',

      // Preview pane
      renderingPreview: 'Merender…',
      previewFailedPrefix: 'Pratinjau gagal dimuat: ',

      // Markdown editor / image uploads
      uploadFailedFallback: 'Unggah gagal',
      bodyPlaceholder: 'Tulis dalam Markdown… tempel atau seret gambar untuk mengunggahnya langsung di sini.',
      uploadingText: 'Mengunggah…',
      imageUploadFailedPrefix: 'Unggah gambar gagal: ',

      // Delete dialog
      deleteCta: 'Hapus',
      deleteDialogTitle: ({ title }: { title: string }) => `Hapus "${title}"?`,
      deleteDialogDescriptionPrefix: 'Menghapus permanen postingan ini. Ini tidak bisa dibatalkan. Ketik',
      deleteDialogDescriptionSuffix: ' untuk konfirmasi.',
      confirmPostTitleAriaLabel: 'Konfirmasi judul postingan',
      cancelCta: 'Batal',
      deletingCta: 'Menghapus…',
      deletePostCta: 'Hapus postingan',

      // Author dialog
      editAuthorDialogTitle: ({ name }: { name: string }) => `Edit ${name}`,
      newAuthorDialogTitle: 'Penulis baru',
      authorDialogDescription: 'Hanya nama penulis sungguhan — jangan buat identitas fiktif.',
      authorNameLabel: 'Nama',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Peran',
      authorRolePlaceholder: 'Founder',
      authorAvatarLabel: 'URL avatar',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Bio',
      authorPersonaLabel: 'Persona (internal)',
      authorPersonaPlaceholder:
        'Menulis dengan kalimat pendek dan langsung. Skeptis pada hype. Lebih suka contoh konkret daripada kata sifat.',
      authorPersonaHint: 'Catatan singkat untuk menulis dengan suara byline ini — tidak pernah dipublikasikan, tidak pernah ditampilkan ke pembaca.',
      authorSavingCta: 'Menyimpan…',
      authorAddingCta: 'Menambahkan…',
      saveAuthorCta: 'Simpan penulis',
      addAuthorCta: 'Tambah penulis',

      // Writing guide panel (spec §G)
      writingGuideHeading: 'Panduan menulis',
      writingGuideSubtitle: 'Dukungan Markdown, gambar, dan prompt briefing AI',
      supportedMarkdownHeading: 'Markdown yang didukung',
      mdListHeadings: 'Heading, level 1 sampai 6',
      mdListLists: 'Daftar berpoin dan bernomor',
      mdListLinksPrefix: 'Tautan — ',
      mdListLinksSuffix: ', hanya http(s) dan mailto',
      mdListImages: 'Gambar di mana pun di dalam isi, termasuk di tengah artikel — tempel atau seret satu masuk (lihat di bawah)',
      mdListCode: 'Blok kode inline dan fenced, dengan syntax highlight',
      mdListTables: 'Tabel gaya GitHub, dengan perataan kolom',
      mdListBlockquotes: 'Blockquote',
      strippedHeading: 'Apa yang dihapus',
      strippedPrefix: 'Semua yang lain. Tag HTML mentah, dan konten ',
      strippedSuffix:
        ' secara khusus, tidak pernah bertahan — isinya disaring dengan daftar izin tetap (heading, daftar, tautan, gambar, kode, tabel, blockquote, plus paragraf/penekanan yang membentuknya) sebelum dirender di mana pun, termasuk di pratinjau ini.',
      imagesHeading: 'Gambar',
      imagesPrefix: 'Tempel atau seret gambar ke dalam isi — ia diunggah ke cdn.embent.com dan disisipkan',
      imagesSuffix:
        ' di posisi kursor, di mana pun kursormu berada, jadi bisa jatuh di tengah paragraf. Gambar sampul di atas adalah field terpisah — tidak pernah berasal dari yang ditaruh di isi.',
      copiedCta: 'Tersalin!',
      copyAiPromptCta: 'Salin prompt AI',
      copiedToClipboard: 'Tersalin ke clipboard.',
      clipboardUnavailable: 'Clipboard tidak tersedia di browser ini — pilih dan salin prompt di bawah.',
      assemblesPrefix: 'Menyusun gaya khas kami, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) => `persona ${authorName}`,
      assemblesNoAuthorClause: 'catatan bahwa belum ada penulis dipilih',
      assemblesSuffix: ', konvensi Markdown di atas, dan struktur yang diminta — tempel ke model eksternal, lalu tempel draf-nya kembali ke isi.',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Tulis dengan suara ${authorName}:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} belum punya persona yang diatur — tulis dengan suara polos dan langsung.`,
      aiPromptAuthorFallbackLower: 'penulis ini',
      aiPromptAuthorFallbackUpper: 'Penulis ini',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Kamu sedang menulis draf postingan blog untuk Embent (embent.com), platform AI agent untuk website.

Gaya khas kami:
- Langsung dan konkret. Tanpa hype, tanpa basa-basi marketing.
- Paragraf pendek (2-4 kalimat).
- Jangan pernah mengarang statistik atau nama pelanggan. Kalau tidak punya angka atau pelanggan sungguhan, jangan dibuat-buat.

Suara penulis:
${personaSection}

Konvensi Markdown (INI SAJA yang akan dirender — selain ini akan dihapus):
- Heading (#, ##, ###), daftar berpoin dan bernomor, blockquote.
- Tautan sebagai [teks](url) — hanya http(s) dan mailto.
- Gambar di mana pun di dalam isi, termasuk di tengah artikel, sebagai ![alt](url).
- Blok kode inline dan fenced.
- Tabel (gaya GitHub).
- Tag HTML mentah dan <script>/<style> dihapus seluruhnya — tulis Markdown polos saja, tanpa HTML.

Struktur yang diminta:
1. Judul
2. Satu paragraf pembuka
3. 4-7 bagian, masing-masing dengan heading h2 (##)
4. Penutup singkat

Tulis draf lengkapnya sekarang sebagai Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Belum ditangani',
        all: 'Semua',
      },
      typeLabel: {
        bug: 'Bug',
        feature: 'Fitur',
        question: 'Pertanyaan',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) => `Re: [Masukan Embent ${id}] ${type}`,
      replyCta: 'Balas',
      colDate: 'Tanggal',
      colWorkspace: 'Workspace',
      colUser: 'Pengguna',
      colType: 'Jenis',
      colMessage: 'Pesan',
      colActions: 'Aksi',
      emptyUnhandled: 'Tidak ada masukan yang belum ditangani — semua beres.',
      emptyAll: 'Belum ada masukan.',
      updateFeedbackErrorFallback: 'Gagal memperbarui masukan ini.',
      reopeningCta: 'Membuka lagi…',
      markingCta: 'Menandai…',
      reopenCta: 'Buka lagi',
      markHandledCta: 'Tandai ditangani',
      noBotFallback: 'tanpa bot',
      noUaFallback: 'tanpa UA',
    },
    playground: {
      demosAllTimeLabel: 'Demo sepanjang waktu',
      demos30dLabel: 'Demo · 30h',
      messagesAllTimeLabel: 'Pesan sepanjang waktu',
      signedUpAllTimeLabel: 'Mendaftar',
      signedUp30dLabel: 'Mendaftar · 30h',
      leadsAllTimeLabel: 'Lead tertangkap',
      leads30dLabel: 'Lead · 30h',
      demosPerDayLabel: 'Demo per hari',
      messagesPerDayLabel: 'Pesan per hari',
      demoUnit: (): string => 'demo',
      messageUnit: (): string => 'pesan',
      noDemos30d: 'Tidak ada demo dalam 30 hari terakhir.',
      statusBreakdownHeading: 'Rincian status (30h)',
      recentDomainsHeading: 'Domain terbaru',
      noDemosYet: 'Belum ada demo.',
      colHostname: 'Hostname',
      colStatus: 'Status',
      colPages: 'Halaman',
      colMessages: 'Pesan',
      colAttempts: 'Percobaan',
      colSignedUp: 'Mendaftar',
      colLeads: 'Lead',
      colCreated: 'Dibuat',
      signedUpAriaLabel: 'sudah mendaftar',
      notSignedUpAriaLabel: 'belum mendaftar',
      noEmailFallback: '(tidak ada email)',
    },
  },
} satisfies Catalog;
